import { Mark, mergeAttributes } from '@tiptap/core';
import type { StyleParseRule } from '@tiptap/pm/model';

export interface SuperscriptExtensionOptions {
    /**
     * HTML attributes to add to the superscript element.
     * @default {}
     * @example { class: 'foo' }
     */
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        superscript: {
            /**
             * Set a superscript mark
             * @example editor.commands.setSuperscript()
             */
            setSuperscript: () => ReturnType;
            /**
             * Toggle a superscript mark
             * @example editor.commands.toggleSuperscript()
             */
            toggleSuperscript: () => ReturnType;
            /**
             * Unset a superscript mark
             *  @example editor.commands.unsetSuperscript()
             */
            unsetSuperscript: () => ReturnType;
        };
    }
}

/**
 * This extension allows you to create superscript text.
 * @see https://www.tiptap.dev/api/marks/superscript
 */
export const WordSuperscript = Mark.create<SuperscriptExtensionOptions>({
    name: 'superscript',

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    parseHTML() {
        return [
            {
                tag: 'sup',
            },
            {
                style: 'vertical-align',
                getAttrs(value) {
                    // Don’t match this rule if the vertical align isn’t super.
                    if (value !== 'super') {
                        return false;
                    }

                    // If it falls through we’ll match, and this mark will be applied.
                    return null;
                },
            } as StyleParseRule,
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['sup', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands() {
        return {
            setSuperscript:
                () =>
                ({ chain }) => {
                    return chain().focus().setMark(this.name).syncParagraphBreakFontWithUpdatedMark(this.name).run();
                },
            toggleSuperscript:
                () =>
                ({ chain }) => {
                    return chain().focus().toggleMark(this.name).syncParagraphBreakFontWithUpdatedMark(this.name).run();
                },
            unsetSuperscript:
                () =>
                ({ chain }) => {
                    return chain().focus().unsetMark(this.name).syncParagraphBreakFontWithUpdatedMark(this.name).run();
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            'Mod-.': () => this.editor.commands.toggleSuperscript(),
        };
    },
});
